import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useCreateGeoSchemeMutation,
  useGetGeoObjectsQuery,
  useTransitionGeoSchemeMutation,
  useUploadGeoVersionMutation,
} from '@shared/api';
import { WORK_TYPES, type GeoWorkType } from '@entities/geodesy';
import { useActiveProjectContext } from '@shared/hooks/useActiveProjectContext';
import { Button } from '@shared/ui/Button';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { Input, Select } from '@shared/ui/Input';
import { Modal } from '@shared/ui/Modal';
import {
  Check,
  ChoiceCard,
  ChoiceGrid,
  ErrorText,
  FieldGrid,
  FieldLabel,
  FieldNote,
  Row,
  Stack,
  useAction,
} from '@widgets/GeoSchemes';

/**
 * «Новая ИС»: наименование, тип работ, объект/площадка, объект для штампа.
 * Два пути: пошагово в ОСНОВЕ (лист, привязка, съёмка — схему чертит
 * программа) или готовая ИС из AutoCAD — файл DXF/PDF становится версией и
 * сразу уходит в ПТО, без шагов мастера.
 */
export function NewSchemeDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { activeProjectId, activeProjectName, setActiveProject } = useActiveProjectContext();
  const { data: objects } = useGetGeoObjectsQuery();
  const [create] = useCreateGeoSchemeMutation();
  const [uploadVersion] = useUploadGeoVersionMutation();
  const [transition] = useTransitionGeoSchemeMutation();
  const { runValue, busy, error } = useAction();

  const [mode, setMode] = useState<'wizard' | 'cad'>('wizard');
  const [cadFile, setCadFile] = useState<File | null>(null);
  const [sendToPto, setSendToPto] = useState(true);
  /** ИС уже создана, а загрузка файла сорвалась — повтор не плодит дубль. */
  const [createdId, setCreatedId] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [workType, setWorkType] = useState<GeoWorkType>('kzh');
  const [projectId, setProjectId] = useState(activeProjectId ?? '');
  const [siteId, setSiteId] = useState('');
  const [elementId, setElementId] = useState('');
  const [objectName, setObjectName] = useState(activeProjectName ?? '');

  const projects = objects?.projects ?? [];
  const sites = objects?.sites ?? [];
  const allElements = objects?.elements ?? [];
  const elements = allElements.filter(
    (el) =>
      (!siteId || el.site_id === siteId) &&
      (!projectId || !el.project_id || el.project_id === projectId),
  );

  function pickProject(id: string) {
    setProjectId(id);
    // Имя объекта для штампа по умолчанию — имя проекта, если человек его ещё не задал.
    const name = projects.find((p) => p.id === id)?.name;
    setActiveProject(id || null, { name });
    if (name && objectName.trim() === '') setObjectName(name);
  }

  function pickElement(id: string) {
    setElementId(id);
    const el = allElements.find((x) => x.id === id);
    if (el) {
      if (!siteId && el.site_id) setSiteId(el.site_id);
      if (!projectId && el.project_id) pickProject(el.project_id);
    }
  }

  const createScheme = () =>
    create({
      title: title.trim(),
      work_type: workType,
      ...(projectId ? { project_id: projectId } : {}),
      ...(siteId ? { site_id: siteId } : {}),
      ...(elementId ? { element_id: elementId } : {}),
      ...(objectName.trim() ? { object_name: objectName.trim() } : {}),
    }).unwrap();

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    if (mode === 'wizard') {
      const scheme = await runValue(createScheme, 'Схема не создана.');
      if (scheme) navigate(`/agents/geodesy/schemes/${scheme.id}`);
      return;
    }
    if (!cadFile) return;
    const id = await runValue(async () => {
      const schemeId = createdId ?? (await createScheme()).id;
      setCreatedId(schemeId);
      await uploadVersion({
        id: schemeId,
        file: cadFile,
        comment: 'Готовая ИС из AutoCAD',
      }).unwrap();
      if (sendToPto) await transition({ id: schemeId, action: 'submit' }).unwrap();
      return schemeId;
    }, 'Готовая ИС не загружена.');
    if (id) navigate(`/agents/geodesy/schemes/${id}`);
  }

  return (
    <Modal open title="Новая исполнительная схема" onClose={busy ? () => undefined : onClose}>
      <form onSubmit={(e) => void submit(e)}>
        <Stack $gap={4}>
          <ChoiceGrid $min={220} role="radiogroup" aria-label="Как делать схему">
            <ChoiceCard
              name="new-scheme-mode"
              checked={mode === 'wizard'}
              onSelect={() => setMode('wizard')}
              title="Пошагово в ОСНОВЕ"
            >
              Лист РД, привязка, съёмка — схему с отклонениями начертит программа.
            </ChoiceCard>
            <ChoiceCard
              name="new-scheme-mode"
              checked={mode === 'cad'}
              onSelect={() => setMode('cad')}
              title="Готовая ИС из AutoCAD"
            >
              Начертили сами — загрузите DXF или PDF, схема уйдёт в ПТО без шагов мастера.
            </ChoiceCard>
          </ChoiceGrid>

          <FieldLabel>
            Наименование
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Свайное поле, оси 1–8 / А–Д"
              autoFocus
              required
            />
          </FieldLabel>

          <Stack $gap={2}>
            <FieldLabel as="div">Тип работ</FieldLabel>
            <ChoiceGrid $min={110} role="radiogroup" aria-label="Тип работ">
              {WORK_TYPES.map((w) => (
                <ChoiceCard
                  key={w.code}
                  name="new-scheme-work-type"
                  checked={workType === w.code}
                  onSelect={() => setWorkType(w.code)}
                  title={w.short}
                >
                  {w.title}
                </ChoiceCard>
              ))}
            </ChoiceGrid>
          </Stack>

          <FieldGrid $min={200}>
            <FieldLabel>
              Объект (проект ПТО)
              <Select value={projectId} onChange={(e) => pickProject(e.target.value)}>
                <option value="">— не выбран —</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </FieldLabel>
            <FieldLabel>
              Площадка
              <Select value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                <option value="">— не выбрана —</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </FieldLabel>
          </FieldGrid>

          <FieldLabel>
            Конструктивный элемент (из справочника РД / площадки)
            <Select value={elementId} onChange={(e) => pickElement(e.target.value)}>
              <option value="">— не выбран —</option>
              {elements.map((el) => (
                <option key={el.id} value={el.id}>
                  {el.code ? `${el.code} — ${el.title}` : el.title}
                  {el.axes ? ` (оси ${el.axes})` : ''}
                </option>
              ))}
            </Select>
          </FieldLabel>

          <FieldLabel>
            Наименование объекта для штампа
            <Input
              value={objectName}
              onChange={(e) => setObjectName(e.target.value)}
              placeholder="Графа 2 основной надписи"
            />
            <FieldNote>Можно поправить позже на шаге «Черновик по ГОСТ».</FieldNote>
          </FieldLabel>

          {mode === 'cad' && (
            <Stack $gap={2}>
              <FileDropZone
                accept=".pdf,.dxf"
                compact
                disabled={busy}
                primary={cadFile ? `Файл: ${cadFile.name}` : 'Готовая ИС: DXF или PDF из AutoCAD'}
                hint={
                  cadFile
                    ? 'нажмите, чтобы выбрать другой'
                    : 'перетащите или нажмите, чтобы выбрать'
                }
                onFile={setCadFile}
              />
              <Check checked={sendToPto} onChange={setSendToPto}>
                Сразу передать в ПТО (ОСНОВА.Инженер)
              </Check>
              <FieldNote>
                Съёмки и проекта в ОСНОВЕ у такой схемы нет — содержание проверит инженер ПТО;
                статус, замечания и версии — как у остальных ИС.
              </FieldNote>
            </Stack>
          )}

          {error && <ErrorText role="alert">{error}</ErrorText>}
          <Row $gap={2}>
            <Button
              type="submit"
              $variant="primary"
              disabled={busy || !title.trim() || (mode === 'cad' && !cadFile)}
            >
              {busy
                ? mode === 'cad'
                  ? 'Загружаем…'
                  : 'Создание…'
                : mode === 'cad'
                  ? sendToPto
                    ? 'Загрузить и передать в ПТО'
                    : 'Загрузить и открыть'
                  : 'Создать и открыть'}
            </Button>
            <Button type="button" $variant="secondary" onClick={onClose} disabled={busy}>
              Отмена
            </Button>
          </Row>
        </Stack>
      </form>
    </Modal>
  );
}
